import { type NavigationScreenState, transformNavigationScreen } from '@gitbook/app-navigation';

import type { NavigationState, NavigationTarget } from './types';

/**
 * Get the location for target operation.
 */
export function getNavigationLocationFor(
    currentState: NavigationState,
    to: NavigationTarget,
    stack: NavigationState[]
):
    | undefined
    | {
          /** New location to push/replace to the history */
          location: NavigationScreenState;
          background: NavigationScreenState | undefined;
          /** Relative movement in the history */
          go: number;
      } {
    // Open a screen on the overlay
    // put the current screen as a background.
    if ('overlay' in to && to.overlay !== null && to.overlay !== undefined) {
        const overlay = transformNavigationScreen(
            currentState.overlay || currentState.screen,
            to.overlay
        );

        // Preserve current background if we already have an overlay opened
        const background =
            'screen' in to
                ? transformNavigationScreen(currentState.screen, to.screen)
                : currentState.screen;
        return {
            location: overlay,
            background,
            go: 0,
        };
    }

    // Close the overlay
    // And navigate to background screen or defaults to "screen"
    if ('overlay' in to && to.overlay === null) {
        const currentOverlay = currentState.overlay;

        if (!currentOverlay) {
            // we only want to use the to.screen if there wasn't
            // any background screen (for example if the user
            // refreshed while seeing the overlay)
            const previousScreen: NavigationScreenState = { type: 'home' };
            return {
                location:
                    'screen' in to && to.screen
                        ? transformNavigationScreen(previousScreen, to.screen)
                        : previousScreen,
                background: undefined,
                go: 0,
            };
        }

        // Find the last screen that is not an overlay
        // we will go back to the point where we opened
        // the overlay instead of navigating further
        const go = rewindHistory(stack, (state) => {
            return !!state.overlay;
        });

        return {
            location: currentState.screen,
            background: undefined,
            go,
        };
    }

    // Navigate the entire screen, replacing overlay/background
    if ('screen' in to) {
        const newScreen = transformNavigationScreen(currentState.screen, to.screen);

        const hadOverlay = hasInScreenOverlay(currentState.screen);
        const hasOverlay = hasInScreenOverlay(newScreen);
        let go = 0;

        if (hadOverlay && !hasOverlay) {
            // Closing the overlay, we go back to the last screen that is not an overlay
            go = rewindHistory(stack, (state) => {
                return hasInScreenOverlay(state.screen);
            });
        }

        return {
            location: newScreen,
            background: undefined,
            go,
        };
    }

    // Update background screen
    if ('background' in to) {
        if (!currentState.overlay) {
            // If we are not in an overlay, we don't have a background to update
            return;
        }

        return {
            location: currentState.overlay,
            background: transformNavigationScreen(currentState.screen, to.background),
            go: 0,
        };
    }

    // Update current screen (it could be overlay or main screen)
    if ('current' in to) {
        return {
            location: transformNavigationScreen(
                currentState.overlay || currentState.screen,
                to.current
            ),
            background: currentState.overlay ? currentState.screen : undefined,
            go: 0,
        };
    }
}

/**
 * Test if a screen has an inline overlay (side-sheet) opened.
 */
function hasInScreenOverlay(screen: NavigationScreenState) {
    return 'sideSheet' in screen && !!screen.sideSheet;
}

/**
 * Return the movement in the history to get to the latest state prior to where `predictate` returns `true`.
 * Calling `history.go(go)` will bring you to replace the state where `predictate` returns `true`.
 */
function rewindHistory(
    stack: NavigationState[],
    predicate: (state: NavigationState) => boolean
): number {
    if (stack.length === 0) {
        return 0;
    }

    let go = 0;
    for (let index = stack.length - 1; index >= 0; index--) {
        go = index - stack.length;
        if (!predicate(stack[index])) {
            break;
        }
    }

    // Ensure we can't go back further than the first state
    // to avoid hard-refresh
    go = Math.max(go, -(stack.length - 1));

    return go;
}
